import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Pencil, Plus, StickyNote } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import type {
  EpisodeNotePreview,
  HotboxTaskResponse,
} from '../../services/api/types.gen';
import { EpisodeNotesPill } from '../episode/EpisodeNotesPill';

export type HotboxNotesCellProps = {
  task: HotboxTaskResponse;
  onOpen: (
    task: HotboxTaskResponse,
    event: React.MouseEvent<HTMLButtonElement>
  ) => void;
};

function sortEpisodeNotesNewestFirst(
  notes: EpisodeNotePreview[]
): EpisodeNotePreview[] {
  return [...notes].sort(
    (left, right) =>
      new Date(right.created_at).getTime() - new Date(left.created_at).getTime()
  );
}

/**
 * Hotbox Notes column cell: yellow task sticky (editable) and one purple
 * episode sticky (read-only). Purple opens a popover listing every episode note.
 */
export const HotboxNotesCell: React.FC<HotboxNotesCellProps> = ({
  task,
  onOpen,
}) => {
  const noteRef = useRef<HTMLSpanElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const episodeNotes = sortEpisodeNotesNewestFirst(
    task.episode_notes_previews ?? []
  );

  const updateTruncation = useCallback(() => {
    const note = noteRef.current;
    setIsTruncated(Boolean(note && note.scrollWidth > note.clientWidth));
  }, []);

  useEffect(() => {
    updateTruncation();
  }, [task.notes, updateTruncation]);

  let taskTrigger: React.ReactNode;
  if (!task.notes) {
    taskTrigger = (
      <button
        type='button'
        className='group flex cursor-pointer items-center gap-1 text-left text-muted-foreground hover:text-foreground'
        aria-label={`Add task notes for ${task.patient_name}`}
        onClick={event => onOpen(task, event)}
      >
        <span>Add note</span>
        <Plus className='h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-70 group-focus-visible:opacity-70' />
      </button>
    );
  } else {
    const trigger = (
      <button
        type='button'
        className='group inline-flex min-w-0 max-w-[9rem] shrink cursor-pointer items-center gap-1 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-left text-xs font-medium text-amber-800 hover:bg-amber-100'
        aria-label={`Edit task notes for ${task.patient_name}`}
        data-testid='task-notes-preview'
        onClick={event => onOpen(task, event)}
        onFocus={updateTruncation}
        onMouseEnter={updateTruncation}
      >
        <StickyNote className='h-3 w-3 shrink-0' aria-hidden='true' />
        <span ref={noteRef} className='min-w-0 truncate'>
          {task.notes}
        </span>
        <Pencil className='h-3 w-3 shrink-0 opacity-70' aria-hidden='true' />
      </button>
    );

    taskTrigger = !isTruncated ? (
      trigger
    ) : (
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent
          side='top'
          collisionPadding={12}
          className='max-w-sm whitespace-pre-line'
        >
          {task.notes}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div className='flex min-w-0 flex-nowrap items-center gap-1'>
        {taskTrigger}
        {episodeNotes.length > 0 ? (
          <EpisodeNotesPill
            notes={episodeNotes}
            ariaLabel={`View episode notes for ${task.patient_name}`}
          />
        ) : null}
      </div>
    </TooltipProvider>
  );
};

export default HotboxNotesCell;
